// DiffMark — the inline insertion/deletion mark for in-material diffs, pure.
//
// This is the one place the diff-legibility rule the AI-proposal loop
// depends on is centralised: a changed span of the material is wrapped so a
// reader can see WHAT changed and WHICH WAY, without relying on colour.
//
// Two variants, a discriminated `DiffMarkVariant` union — `add` | `remove`
// (`variant` is the house discriminant-prop name, shared with Button/
// IconButton/Skeleton). This is a genuine structural discriminant (the two
// are different diff operations with different semantics and different host
// elements), not a boolean flag swapping one element's meaning: each variant
// renders its own native element, `<ins>` or `<del>`, so the insertion/
// deletion semantics live in the DOM, not just in a class name.
//
// Colour is NEVER the sole channel (SC 1.4.1). Each variant pairs its tint
// with TWO redundant non-colour channels: a leading glyph (`+` / `−`) and a
// text decoration (underline for an insertion, strike-through for a
// deletion). On top of that a VisuallyHidden prefix ("Inserted:" /
// "Deleted:") spells the meaning for assistive tech, since `<ins>`/`<del>`
// role support is uneven across screen readers. The glyph is aria-hidden —
// it reinforces the visual, it is not the accessible name.
//
// Pure/props-driven: `variant` picks the element + look, `children` is the
// changed content. No state, no effects, no domain knowledge — it decorates
// whatever span it is handed.

import { Trans } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden.js";
import "./DiffMark.css";

export type DiffMarkVariant = "add" | "remove";

export type DiffMarkProps = {
    readonly variant: DiffMarkVariant;
    readonly children: ReactNode;
    // Optional layout hook for the composing context; never used to recolour
    // the mark (the tint/decoration is fixed by `variant`).
    readonly className?: string;
};

export const DiffMark = ({ variant, children, className }: DiffMarkProps) => {
    const cn = `bilda-diff-mark bilda-diff-mark--${variant}${
        className ? ` ${className}` : ""
    }`;
    // Branch per variant to keep the host element concrete (`ins` vs `del`) —
    // no dynamic tag and no cast, the same discipline as VisuallyHidden.
    return variant === "add" ? (
        <ins className={cn}>
            <VisuallyHidden>
                <Trans>Inserted:</Trans>{" "}
            </VisuallyHidden>
            <span className="bilda-diff-mark__glyph" aria-hidden="true">
                +
            </span>
            <span className="bilda-diff-mark__content">{children}</span>
        </ins>
    ) : (
        <del className={cn}>
            <VisuallyHidden>
                <Trans>Deleted:</Trans>{" "}
            </VisuallyHidden>
            <span className="bilda-diff-mark__glyph" aria-hidden="true">
                −
            </span>
            <span className="bilda-diff-mark__content">{children}</span>
        </del>
    );
};
